/**
 * Lead Stage History Service – Firestore-backed
 *
 * Replaces the localStorage-based stage history tracking with durable
 * Firestore storage. Each stage transition is written as an immutable
 * document in the `leadStageHistory` collection, providing:
 *
 * - Cross-device visibility (no more per-browser data silos)
 * - Immutable audit trail (security rules prevent edits/deletes)
 * - Queryable KPIs (by lead, user, date range, status, etc.)
 */
import { db } from '@/lib/firebase';
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  limit,
} from 'firebase/firestore';

export interface LeadStageChange {
  leadId: string;
  fromStatus: string;
  toStatus: string;
  changedAt: Date;
  changedBy: string; // dsaUserId
  officeId?: string;
}

interface FirestoreStageChange extends Omit<LeadStageChange, 'changedAt'> {
  changedAt: Timestamp;
}

const COLLECTION = 'leadStageHistory';

/**
 * Record a lead stage transition.
 */
export async function addLeadStageChange(
  change: LeadStageChange,
): Promise<string> {
  const docRef = await addDoc(collection(db, COLLECTION), {
    leadId: change.leadId,
    fromStatus: change.fromStatus,
    toStatus: change.toStatus,
    changedAt: Timestamp.fromDate(change.changedAt),
    changedBy: change.changedBy,
    officeId: change.officeId || '',
  } satisfies FirestoreStageChange);

  return docRef.id;
}

/**
 * Get stage history for a specific lead, ordered newest-first.
 */
export async function getLeadStageHistory(
  leadId: string,
  maxResults = 50,
): Promise<LeadStageChange[]> {
  const q = query(
    collection(db, COLLECTION),
    where('leadId', '==', leadId),
    orderBy('changedAt', 'desc'),
    limit(maxResults),
  );

  const snap = await getDocs(q);
  return snap.docs.map((doc) => {
    const d = doc.data() as FirestoreStageChange;
    return {
      leadId: d.leadId,
      fromStatus: d.fromStatus,
      toStatus: d.toStatus,
      changedAt: d.changedAt.toDate(),
      changedBy: d.changedBy,
      officeId: d.officeId,
    };
  });
}

/**
 * Get all stage changes by a specific user within a date range.
 * Useful for dashboard KPIs (e.g. "conversions this month").
 */
export async function getStageChangesByUser(
  userId: string,
  startDate: Date,
  endDate: Date,
): Promise<LeadStageChange[]> {
  const q = query(
    collection(db, COLLECTION),
    where('changedBy', '==', userId),
    where('changedAt', '>=', Timestamp.fromDate(startDate)),
    where('changedAt', '<=', Timestamp.fromDate(endDate)),
    orderBy('changedAt', 'desc'),
  );

  const snap = await getDocs(q);
  return snap.docs.map((doc) => {
    const d = doc.data() as FirestoreStageChange;
    return {
      leadId: d.leadId,
      fromStatus: d.fromStatus,
      toStatus: d.toStatus,
      changedAt: d.changedAt.toDate(),
      changedBy: d.changedBy,
      officeId: d.officeId,
    };
  });
}

/**
 * Count transitions to a specific status within a date range.
 * Example: count disbursements this month.
 */
export async function countTransitionsToStatus(
  userId: string,
  toStatus: string,
  startDate: Date,
  endDate: Date,
): Promise<number> {
  const changes = await getStageChangesByUser(userId, startDate, endDate);
  return changes.filter((c) => c.toStatus === toStatus).length;
}
